<script>
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useFacebookPageConnect } from 'dashboard/composables/useFacebookPageConnect';
import { required } from '@vuelidate/validators';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import MetaIntegrationConfigAPI from 'dashboard/api/metaIntegrationConfig';

import PageHeader from '../../SettingsSubPageHeader.vue';
import router from '../../../../index';
import { useBranding } from 'shared/composables/useBranding';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

import * as Sentry from '@sentry/vue';

export default {
  components: {
    LoadingState,
    PageHeader,
    NextButton,
    ComboBox,
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    const { preloadSdk, loginAndFetchPages } = useFacebookPageConnect();
    return {
      replaceInstallationName,
      preloadSdk,
      loginAndFetchPages,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      isCreating: false,
      hasError: false,
      user_access_token: '',
      selectedPage: { name: null, id: null },
      pageName: '',
      pageList: [],
      emptyStateMessage: this.$t('INBOX_MGMT.DETAILS.LOADING_FB'),
      errorStateMessage: '',
      errorStateDescription: '',
      hasLoginStarted: false,
      isSavingConfig: false,
      metaConfig: {
        app_id: '',
        app_secret: '',
        verify_token: '',
        api_version: 'v22.0',
        app_secret_configured: false,
        verify_token_configured: false,
      },
    };
  },

  validations: {
    pageName: {
      required,
    },

    selectedPage: {
      isEmpty() {
        return this.selectedPage !== null && !!this.selectedPage.name;
      },
    },
  },

  computed: {
    showLoader() {
      return !this.user_access_token || this.isCreating;
    },
    getSelectablePages() {
      return this.pageList.filter(item => !item.exists);
    },
    comboBoxPageOptions() {
      return this.getSelectablePages.map(({ id, name }) => ({
        value: id,
        label: name,
      }));
    },
    hasFacebookConfig() {
      return (
        this.metaConfig.app_id &&
        (this.metaConfig.app_secret || this.metaConfig.app_secret_configured) &&
        (this.metaConfig.verify_token ||
          this.metaConfig.verify_token_configured)
      );
    },
  },

  mounted() {
    this.fetchMetaConfig();
    // Warm the SDK so the login click opens its popup within the gesture's
    // activation window (see useFacebookPageConnect).
    if (window.chatwootConfig?.fbAppId) {
      this.preloadSdk();
    }
  },

  methods: {
    async fetchMetaConfig() {
      try {
        const { data } = await MetaIntegrationConfigAPI.get();
        const facebook = data.facebook || {};
        this.metaConfig = {
          app_id: facebook.app_id || '',
          app_secret: '',
          verify_token: '',
          api_version: facebook.api_version || 'v22.0',
          app_secret_configured: !!facebook.app_secret_configured,
          verify_token_configured: !!facebook.verify_token_configured,
        };
      } catch (error) {
        // The normal channel flow still works for authorized account admins.
      }
    },

    syncFacebookConfigToWindow(data = {}) {
      const facebook = data.facebook || {};
      window.chatwootConfig = {
        ...window.chatwootConfig,
        fbAppId: facebook.app_id || window.chatwootConfig?.fbAppId,
        fbApiVersion:
          facebook.api_version || window.chatwootConfig?.fbApiVersion,
      };
    },

    async saveFacebookConfig() {
      this.isSavingConfig = true;
      try {
        const { data } = await MetaIntegrationConfigAPI.updateConfig({
          facebook: this.metaConfig,
        });
        this.syncFacebookConfigToWindow(data);
        await this.fetchMetaConfig();
        useAlert('Facebook configuration saved');
        if (window.chatwootConfig?.fbAppId) {
          this.preloadSdk();
        }
      } catch (error) {
        useAlert('Could not save Facebook configuration');
      } finally {
        this.isSavingConfig = false;
      }
    },

    async startLogin() {
      if (!this.hasFacebookConfig || !window.chatwootConfig?.fbAppId) {
        this.hasLoginStarted = true;
        this.hasError = true;
        this.errorStateMessage = 'Facebook is not configured for this account.';
        this.errorStateDescription = 'Configure and save the Facebook App ID, App Secret and Verify Token here before connecting Facebook pages.';
        return;
      }
      this.hasLoginStarted = true;
      try {
        const result = await this.loginAndFetchPages();
        if (!result) {
          // Cancelled popup / not authorized — surface a generic auth error.
          this.hasError = true;
          this.errorStateMessage = this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH');
          this.errorStateDescription = '';
          return;
        }
        this.pageList = result.pages;
        this.user_access_token = result.userAccessToken;
      } catch (error) {
        if (error.name === 'ScriptLoaderError') {
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
        } else {
          Sentry.captureException(error);
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
        }
      }
    },

    setPageName(pageId) {
      const page = this.pageList.find(p => p.id === pageId);
      if (page) {
        this.selectedPage = page;
        this.pageName = page.name;
      } else {
        this.selectedPage = { name: null, id: null };
        this.pageName = '';
      }
      this.v$.selectedPage.$touch();
    },

    channelParams() {
      return {
        user_access_token: this.user_access_token,
        page_access_token: this.selectedPage.access_token,
        page_id: this.selectedPage.id,
        inbox_name: this.selectedPage.name?.trim(),
      };
    },

    createChannel() {
      this.v$.$touch();
      if (!this.v$.$error) {
        this.emptyStateMessage = this.$t('INBOX_MGMT.DETAILS.CREATING_CHANNEL');
        this.isCreating = true;
        this.$store
          .dispatch('inboxes/createFBChannel', this.channelParams())
          .then(data => {
            router.replace({
              name: 'settings_inboxes_add_agents',
              params: { page: 'new', inbox_id: data.id },
            });
          })
          .catch(() => {
            this.isCreating = false;
          });
      }
    },
  },
};
</script>

<template>
  <div class="w-full h-full col-span-6 p-6 overflow-auto">
    <div
      v-if="!hasLoginStarted"
      class="flex flex-col items-center justify-center h-full"
    >
      <form
        class="w-full max-w-lg grid gap-4 mb-8"
        @submit.prevent="saveFacebookConfig"
      >
        <div>
          <h3 class="mb-2 text-base font-medium text-n-slate-12">
            Facebook
          </h3>
          <p class="text-sm text-n-slate-11">
            Configure la app de Meta para conectar páginas de Facebook en esta entrada.
          </p>
        </div>
        <label>
          App ID
          <input v-model="metaConfig.app_id" type="text" />
        </label>
        <label>
          App Secret
          <input
            v-model="metaConfig.app_secret"
            type="password"
            :placeholder="
              metaConfig.app_secret_configured
                ? 'Ya configurado. Dejar vacío para conservarlo.'
                : ''
            "
          />
        </label>
        <label>
          Verify Token
          <input
            v-model="metaConfig.verify_token"
            type="password"
            :placeholder="
              metaConfig.verify_token_configured
                ? 'Ya configurado. Dejar vacío para conservarlo.'
                : ''
            "
          />
        </label>
        <label>
          Version API
          <input
            v-model="metaConfig.api_version"
            type="text"
            placeholder="v22.0"
          />
        </label>
        <div class="flex justify-end">
          <NextButton
            type="submit"
            :is-loading="isSavingConfig"
            :label="'Guardar configuración'"
          />
        </div>
      </form>
      <a href="#" class="text-center" @click.prevent="startLogin()">
        <img
          class="w-auto h-10 rounded-md"
          src="~dashboard/assets/images/channels/facebook_login.png"
          alt="Facebook-logo"
        />
      </a>
      <p class="py-6">
        {{ replaceInstallationName($t('INBOX_MGMT.ADD.FB.HELP')) }}
      </p>
    </div>
    <div v-else>
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
      </div>
      <LoadingState v-else-if="showLoader" :message="emptyStateMessage" />
      <form
        v-else
        class="flex flex-col flex-wrap mx-0"
        @submit.prevent="createChannel()"
      >
        <div class="w-full">
          <PageHeader
            :header-title="$t('INBOX_MGMT.ADD.DETAILS.TITLE')"
            :header-content="
              replaceInstallationName($t('INBOX_MGMT.ADD.DETAILS.DESC'))
            "
          />
        </div>
        <div class="w-3/5">
          <div class="w-full mb-2">
            <div class="input-wrap" :class="{ error: v$.selectedPage.$error }">
              <span class="text-n-slate-12 text-start">
                {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PAGE') }}
              </span>
              <ComboBox
                :model-value="selectedPage.id"
                :options="comboBoxPageOptions"
                :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_A_VALUE')"
                :has-error="v$.selectedPage.$error"
                class="[&>div>button]:!bg-n-alpha-black2 mt-1"
                @update:model-value="setPageName"
              />
              <span v-if="v$.selectedPage.$error" class="message mt-0.5">
                {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PLACEHOLDER') }}
              </span>
            </div>
          </div>
          <div class="w-full">
            <label :class="{ error: v$.pageName.$error }">
              {{ $t('INBOX_MGMT.ADD.FB.INBOX_NAME') }}
              <input
                v-model="pageName"
                type="text"
                :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_NAME')"
                @input="v$.pageName.$touch"
              />
              <span v-if="v$.pageName.$error" class="message">
                {{ $t('INBOX_MGMT.ADD.FB.ADD_NAME') }}
              </span>
            </label>
          </div>
          <div class="w-full text-right">
            <NextButton :label="$t('INBOX_MGMT.ADD.FB.CREATE_INBOX')" />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
